import React, { useState, useEffect, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import ChatInput from "../components/ChatInput";
import MarkdownRenderer from "../components/MarkdownRenderer";
import CommandMenu from "../components/CommandMenu";
import TextToSpeech from "../components/TextToSpeech";
import HistoryPanel from "../components/HistoryPanel";
import handleCameraClick from "../components/CameraHandler";
// import LanguageDropdown from '../components/LanguageDropdown'; // Unused for now
import Toast from "../components/Toast";
import SuggestedQuestions from "../components/SuggestedQuestions";
import { useNavigate } from "react-router-dom";
import { useHandleStreamResponse } from "../hooks/useHandleStreamResponse";
import { API_URL } from "./config";
import "../components/KeyboardAware.css"; // Import keyboard aware styles
import { useAuth } from "../context/AuthContext"; // Import useAuth to check authentication status
import UserMenu from "../components/UserMenu";
import { BlippLogo, BlippMark } from "../components/BlippLogo";
import { HelmetProvider } from "react-helmet-async";

// Add getGreeting function to determine time-based greeting
const getGreeting = (language) => {
  const hour = new Date().getHours();

  // Greetings for different languages
  const greetings = {
    en: {
      morning: "Good morning",
      afternoon: "Good afternoon",
      evening: "Good evening",
    },
    sr: {
      morning: "Dobro jutro",
      afternoon: "Dobar dan",
      evening: "Dobro veče",
    },
    es: {
      morning: "Buenos días",
      afternoon: "Buenas tardes",
      evening: "Buenas noches",
    },
    fr: {
      morning: "Bonjour",
      afternoon: "Bon après-midi",
      evening: "Bonsoir",
    },
    de: {
      morning: "Guten Morgen",
      afternoon: "Guten Tag",
      evening: "Guten Abend",
    },
    it: {
      morning: "Buongiorno",
      afternoon: "Buon pomeriggio",
      evening: "Buonasera",
    },
    pt: {
      morning: "Bom dia",
      afternoon: "Boa tarde",
      evening: "Boa noite",
    },
    nl: {
      morning: "Goedemorgen",
      afternoon: "Goedemiddag",
      evening: "Goedenavond",
    },
    pl: {
      morning: "Dobry poranek",
      afternoon: "Dobra popołudnia",
      evening: "Dobry wieczór",
    },
    ru: {
      morning: "Доброе утро",
      afternoon: "Добрый день",
      evening: "Добрый вечер",
    },
    ja: {
      morning: "おはようございます",
      afternoon: "こんにちは",
      evening: "こんばんは",
    },
    ko: {
      morning: "좋은 아침입니다",
      afternoon: "안녕하세요",
      evening: "안녕하세요",
    },
    zh: {
      morning: "早上好",
      afternoon: "下午好",
      evening: "晚上好",
    },
    ar: {
      morning: "صباح الخير",
      afternoon: "مساء الخير",
      evening: "مساء الخير",
    },
    hi: {
      morning: "सुप्रभात",
      afternoon: "नमस्ते",
      evening: "शुभ संध्या",
    },
    bn: {
      morning: "সুপ্রভাত",
      afternoon: "শুভ অপরাহ্ন",
      evening: "শুভ সন্ধ্যা",
    },
  };

  // Default to English if the language is not supported
  const langGreetings = greetings[language] || greetings.en;

  if (hour >= 5 && hour < 12) return langGreetings.morning;
  if (hour >= 12 && hour < 18) return langGreetings.afternoon;
  return langGreetings.evening;
};

const MainComponent = () => {
  const navigate = useNavigate();

  // Remove authentication check to bypass login completely
  const { idToken, isAnonymous } = useAuth(); // Access the current user and logout from the AuthContext , along with Token
  // Removed authentication popup functionality
  // const [showAuthPopup, setShowAuthPopup] = useState(false);
  // const popupTimerRef = useRef(null);
  // const [isFirstPopup, setIsFirstPopup] = useState(true);
  // const [dropdownOpen, setDropdownOpen] = useState(false); // To control dropdown visibility
  const [messages, setMessages] = useState([]);
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  // const [isPageLoading, setIsPageLoading] = useState(true); // Unused for now
  const [language] = useState(() => {
    return localStorage.getItem("preferred-language") || "en";
  });
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedMode = localStorage.getItem("darkMode");
    // BlippAI is dark-first; light mode is opt-in via the toggle
    return savedMode !== null ? savedMode === "true" : true;
  });
  const [showCommandMenu, setShowCommandMenu] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [messagesEndRef, setMessagesEndRef] = useState(null);

  // Add state for test chat mode
  const [useTestChat, setUseTestChat] = useState(() => {
    return localStorage.getItem("useTestChat") === "true";
  });

  // Add state for history panel
  const [isHistoryPanelOpen, setIsHistoryPanelOpen] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [refreshHistory, setRefreshHistory] = useState(0);

  // Add state for toast notifications
  const [toast, setToast] = useState({
    isVisible: false,
    message: "",
    type: "success",
  });

  const showToast = (message, type) => {
    setToast({ isVisible: true, message, type });
    setTimeout(() => setToast({ isVisible: false, message: "", type }), 3000);
  };

  const [autoScroll, setAutoScroll] = useState(true);
  const [pendingFiles, setPendingFiles] = useState([]); // New state for pending files
  const chatContainerRef = useRef(null);
  const [threadId, setThreadId] = useState(null);
  const [tools_flags] = useState({
    web_search: { enabled: true },
    code_interpreter: { enabled: false },
    reasoning: { enabled: false },
    image_generation: { enabled: false },
  });

  const handleDeleteSession = (sessionId) => {
    if (sessionId === currentSessionId) {
      setMessages([]);
      setThreadId(null);
      setCurrentSessionId(null);
      setQuery("");
      setSuggestedQuestions([]);
      setPendingFiles([]);
      showToast("Active chat session deleted, started new chat", "info");
    }
    setRefreshHistory((prev) => prev + 1); // Trigger history refresh
  };

  // Add state to track if a message is currently streaming
  const [isStreaming, setIsStreaming] = useState(false); // New state for streaming

  const { handleResponse } = useHandleStreamResponse({
    onChunk: (chunk) => {
      setMessages((prev) => {
        const lastMessage = prev[prev.length - 1];
        if (lastMessage && lastMessage.role === "assistant") {
          return [
            ...prev.slice(0, -1),
            {
              ...lastMessage,
              content: lastMessage.content + chunk,
              isStreaming: true,
            },
          ];
        }
        return [
          ...prev,
          { role: "assistant", content: chunk, id: Date.now() + 1 },
        ];
      });
    },
    onFinish: (fullMessage) => {
      setMessages((prev) => {
        const lastMessage = prev[prev.length - 1];
        if (lastMessage && lastMessage.role === "assistant") {
          return [
            ...prev.slice(0, -1),
            { ...lastMessage, content: fullMessage, isStreaming: false },
          ];
        }
        return prev;
      });
      setPendingFiles([]);
      setIsLoading(false);
      setRefreshHistory((prev) => prev + 1); // Trigger history refresh
    },
    onThreadId: (newThreadId) => {
      setThreadId(newThreadId);
      // console.log("Received thread_id:", newThreadId);
    },
    onSuggestedQuestions: (questions) => {
      // console.log("Extracted suggested questions:", questions);
      setSuggestedQuestions(questions);
    },
    onSources: (sources) => {
      // console.log("Extracted sources:", sources);
      setMessages((prev) => {
        const lastMessage = prev[prev.length - 1];
        if (lastMessage && lastMessage.role === "assistant") {
          return [
            ...prev.slice(0, -1),
            {
              ...lastMessage,
              sources: sources,
            },
          ];
        }
        return prev;
      });
    },
    onStreamStart: () => {
      // console.log("Stream started");
      setIsStreaming(true);
    },
    onStreamEnd: () => {
      // console.log("Stream ended");
      setIsStreaming(false);
    },
    onFirstChunk: () => {
      // console.log("First chunk received");
      setIsStreaming(false);
    },
  });

  // Simple login handler for header button
  const handleLogin = () => {
    navigate("/login");
  };

  // Removed authentication popup functionality - no longer needed
  // useEffect(() => {
  //   const handleAuth = async () => {
  //     if (isAnonymous && currentUser) {
  //       const schedulePopup = () => {
  //         const delay = isFirstPopup ? 650 : 15 * 60 * 1000; // 60s or 15 min
  //         popupTimerRef.current = setTimeout(() => {
  //           setShowAuthPopup(true);
  //           setIsFirstPopup(false);
  //         }, delay);
  //       };
  //       schedulePopup();
  //     }
  //   };
  //   handleAuth();
  //   return () => {
  //     if (popupTimerRef.current) {
  //       clearTimeout(popupTimerRef.current);
  //     }
  //   };
  // }, [currentUser, navigate, isAnonymous, isFirstPopup]);

  // Add state for suggested questions
  const [suggestedQuestions, setSuggestedQuestions] = useState([]);

  // Add scroll event listener to detect if user is at the bottom of the chat
  useEffect(() => {
    const chatContainer = chatContainerRef.current;
    if (!chatContainer) return;

    const handleScroll = () => {
      // Calculate how close to the bottom the user is
      const { scrollTop, scrollHeight, clientHeight } = chatContainer;
      const scrollBottom = scrollHeight - scrollTop - clientHeight;

      // More generous threshold - consider "at bottom" if within 150px of the bottom
      const atBottom = scrollBottom < 70;

      // Only re-enable auto-scroll if user has scrolled to the bottom
      if (atBottom && !autoScroll) {
        setAutoScroll(true);
      } else if (!atBottom && autoScroll) {
        setAutoScroll(false);
      }
    };

    chatContainer.addEventListener("scroll", handleScroll, { passive: true });
    return () => chatContainer.removeEventListener("scroll", handleScroll);
  }, [autoScroll]);

  // Smart scroll to bottom when messages change, but only if autoScroll is enabled
  useEffect(() => {
    if (messagesEndRef && autoScroll) {
      messagesEndRef.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, messagesEndRef, autoScroll]);

  // Force scroll to bottom when a new message is sent by the user
  useEffect(() => {
    const lastMessage = messages[messages.length - 1];
    if (lastMessage?.role === "user") {
      if (messagesEndRef) {
        messagesEndRef.scrollIntoView({ behavior: "smooth" });
        setAutoScroll(true);
      }
    }
  }, [messages, messagesEndRef]);

  // Save dark mode preference to localStorage
  useEffect(() => {
    localStorage.setItem("darkMode", isDarkMode);

    // Apply dark mode class to body element for global styling
    if (isDarkMode) {
      document.body.classList.add("dark-mode");
      document.documentElement.classList.add("dark");
    } else {
      document.body.classList.remove("dark-mode");
      document.documentElement.classList.remove("dark");
    }
  }, [isDarkMode]);

  // Save language preference to localStorage
  useEffect(() => {
    localStorage.setItem("preferred-language", language);
  }, [language]);

  // Save test chat preference to localStorage
  useEffect(() => {
    localStorage.setItem("useTestChat", useTestChat);
  }, [useTestChat]);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Add keyboard visibility detection
  const [isKeyboardVisible, setIsKeyboardVisible] = useState(false);

  // Detect keyboard visibility for mobile devices
  useEffect(() => {
    if (!isMobile) return;

    const detectKeyboard = () => {
      // Visual viewport API is more reliable for detecting keyboard
      if (window.visualViewport) {
        const keyboardThreshold = window.innerHeight * 0.15; // 15% threshold
        const heightDiff = window.innerHeight - window.visualViewport.height;
        setIsKeyboardVisible(heightDiff > keyboardThreshold);
      }
    };

    window.visualViewport?.addEventListener("resize", detectKeyboard);
    return () => {
      window.visualViewport?.removeEventListener("resize", detectKeyboard);
    };
  }, [isMobile]);

  const handleSubmit = async () => {
    if (!query.trim() && pendingFiles.length === 0) return; // Allow file-only submissions
    if (isLoading) return; // Prevent submission during loading

    // Clear any previous suggested questions when submitting a new query
    setSuggestedQuestions([]);

    const userMessage = {
      role: "user",
      content:
        query ||
        `Uploaded file(s): ${
          pendingFiles.map((file) => file.name).join(", ") || "attachment"
        }`,
      id: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setQuery("");
    setIsLoading(true);

    try {
      const fileAttachments = {
        has_attachments: pendingFiles.length > 0,
        attachments: pendingFiles.map((file) => ({
          file_type: file.type,
          content: file.data,
        })),
      };

      const conversationHistory = messages.map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      const payload = {
        thread_id: threadId,
        message: userMessage.content,
        language,
        history: conversationHistory,
        file_attachments: fileAttachments,
        tools: tools_flags,
      };

      // console.log("Payload sent to backend:", payload);

      const response = await fetch(`${API_URL}/api/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          thread_id: threadId,
          message: userMessage.content,
          language,
          history: conversationHistory,
          file_attachments: fileAttachments,
          tools: tools_flags,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            errorData.error ||
            `Error: ${response.statusText}`
        );
      }

      // Create an empty assistant message first for streaming
      const assistantMessage = {
        role: "assistant",
        content: "",
        id: Date.now() + 1,
        isStreaming: true, // Set this to true right from the start
      };

      setMessages((prev) => [...prev, assistantMessage]);
      setIsStreaming(true); // Set global streaming state to true immediately

      // Use our custom handleResponse function to process the streaming response
      await handleResponse(response);
    } catch (error) {
      console.error("Error calling chat API:", error);
      const errorMessage = {
        role: "assistant",
        content: error.message.includes("file")
          ? `Error processing file(s): ${error.message}`
          : "Sorry, I'm having trouble connecting to the server. Please try again later.",
        id: Date.now() + 1,
        isError: true,
      };
      setMessages((prev) => [...prev, errorMessage]);
      setIsLoading(false);
    }
  };

  // Define handle Upload / User Attachments function
  const handleUploadClick = () => {
    handleCameraClick({
      isDarkMode,
      onFileSelected: (parsedFile, error) => {
        if (error) {
          setToast({
            isVisible: true,
            message: error,
            type: "error",
          });
          setTimeout(() => {
            setToast({ ...toast, isVisible: false });
          }, 3000);
          return;
        }
        setPendingFiles((prev) => [...prev, parsedFile]);
        setMessages((prev) => [
          ...prev,
          {
            role: "user",
            content:
              parsedFile.type === "image"
                ? `Uploaded image: ${parsedFile.name}`
                : `${parsedFile.name} is uploaded`,
            id: Date.now(),
            file: parsedFile,
          },
        ]);
      },
    });
  };

  // Removed unused extractSuggestedQuestions function
  // const extractSuggestedQuestions = (content) => { ... };

  // Translations for multi-language support
  const translations = {
    en: {
      "Message ChatGPT...": "Ask Anything...",
      "BlippAI may produce inaccurate information about people, places, or facts.":
        "BlippAI may produce inaccurate information about people, places, or facts.",
      "New Chat": "New Chat",
      "Clear Conversations": "Clear Conversations",
      "Dark mode": "Dark mode",
      "Light mode": "Light mode",
      "Log out": "Log out",
      "Continue to iterate?": "Continue to iterate?",
      "Test Chat Mode": "Test Chat Mode",
      "Normal Chat Mode": "Normal Chat Mode",
      // Removed authentication popup translations
      // "Sign in to save your chats": "Sign in to save your chats",
      // "Stay logged out": "Stay logged out",
      // "Login": "Login",
      // "Sign up": "Sign up",
    },
    sr: {
      "Message ChatGPT...": "Poruka BlippAI...",
      "BlippAI may produce inaccurate information about people, places, or facts.":
        "BlippAI može da proizvede netačne informacije o ljudima, mestima ili činjenicama.",
      "New Chat": "Novi razgovor",
      "Clear Conversations": "Obriši razgovore",
      "Dark mode": "Tamni režim",
      "Light mode": "Svetli režim",
      "Log out": "Odjavi se",
      "Continue to iterate?": "Nastaviti iteraciju?",
      "Test Chat Mode": "Test režim razgovora",
      "Normal Chat Mode": "Normalni režim razgovora",
      // Removed authentication popup translations
      // "Sign in to save your chats": "Prijavite se da biste sačuvali svoje razgovore",
      // "Stay logged out": "Ostani odjavljen",
      // "Login": "Prijavi se",
      // "Sign up": "Registruj se",
    },
    ru: {
      "Message ChatGPT...": "Сообщение BlippAI...",
      "BlippAI may produce inaccurate information about people, places, or facts.":
        "BlippAI может предоставлять неточную информацию о людях, местах или фактах.",
      "New Chat": "Новый чат",
      "Clear Conversations": "Очистить диалоги",
      "Dark mode": "Темный режим",
      "Light mode": "Светлый режим",
      "Log out": "Выйти",
      "Continue to iterate?": "Продолжить итерацию?",
      "Test Chat Mode": "Тестовый режим чата",
      "Normal Chat Mode": "Обычный режим чата",
      // Removed authentication popup translations
      // "Sign in to save your chats": "Войдите, чтобы сохранить ваши чаты",
      // "Stay logged out": "Остаться без входа",
      // "Login": "Войти",
      // "Sign up": "Зарегистрироваться",
    },
    ar: {
      "Message ChatGPT...": "...BlippAI رسالة إلى",
      "BlippAI may produce inaccurate information about people, places, or facts.":
        ".قد ينتج معلومات غير دقيقة عن الأشخاص أو الأماكن أو الحقائق BlippAI",
      "New Chat": "محادثة جديدة",
      "Clear Conversations": "مسح المحادثات",
      "Dark mode": "الوضع الداكن",
      "Light mode": "الوضع الفاتح",
      "Log out": "تسجيل الخروج",
      "Continue to iterate?": "هل تريد الاستمرار في التكرار؟",
      "Test Chat Mode": "وضع المحادثة الاختباري",
      "Normal Chat Mode": "وضع المحادثة العادي",
      // Removed authentication popup translations
      // "Sign in to save your chats": "تسجيل الدخول لحفظ محادثاتك",
      // "Stay logged out": "البقاء بدون تسجيل دخول",
      // "Login": "تسجيل الدخول",
      // "Sign up": "التسجيل",
    },
  };

  // Helper function to get translation
  const getTranslation = (key) => {
    return translations[language]?.[key] || translations.en[key] || key;
  };

  // Updated handleNewChat function to clear all relevant states
  const handleNewChat = () => {
    setMessages([]);
    setQuery("");
    setSuggestedQuestions([]);
    setThreadId(null); // Reset threadId for a new backend thread
    setPendingFiles([]); // Clear any pending file uploads
    setCurrentSessionId(null); // Reset current session ID
  };

  // Clear all conversations
  const handleClearConversations = async () => {
    if (isAnonymous) {
      showToast("Sign in to manage chat history", "error");
      return;
    }

    setMessages([]);
    setQuery("");
    setCurrentSessionId(null);
    setThreadId(null);
    setSuggestedQuestions([]);
    setPendingFiles([]);

    try {
      const response = await fetch(`${API_URL}/api/chat-history`, {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to clear chat history");
      }

      showToast("All chat history cleared", "success");
    } catch (err) {
      console.error("Error clearing server chat history:", err);
      showToast(err.message, "error");
    }
  };

  // Toggle history panel
  const toggleHistoryPanel = () => {
    setIsHistoryPanelOpen(!isHistoryPanelOpen);
    // Close command menu if open
    if (showCommandMenu) {
      setShowCommandMenu(false);
    }
  };

  // Toggle test chat mode
  const toggleTestChatMode = () => {
    setUseTestChat(!useTestChat);
    // Clear current chat when switching modes
    setMessages([]);
    setQuery("");
    setSuggestedQuestions([]);
  };

  // Handle selecting a chat session from history
  const handleSelectSession = async (sessionId) => {
    if (isAnonymous && sessionId) {
      showToast("Sign in to view chat history", "error");
      return;
    }

    if (!sessionId) {
      handleNewChat();
      setCurrentSessionId(null);
      showToast("Started a new chat", "success");
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/chat-history/${sessionId}`, {
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Failed to load chat session");
      }

      const data = await response.json();
      if (data.session && data.session.messages) {
        const formattedMessages = data.session.messages.map((msg, index) => ({
          role: msg.role,
          content: msg.content || "",
          id: Date.now() + index,
        }));

        setMessages(formattedMessages);
        setThreadId(sessionId); // Set threadId to sessionId for existing session
        setCurrentSessionId(sessionId);
        setQuery(""); // Clear input field
        setSuggestedQuestions([]); // Clear suggested questions
        setPendingFiles([]); // Clear pending files
        showToast("Chat session loaded", "success");

        if (isMobile) {
          setIsHistoryPanelOpen(false);
        }
      } else {
        throw new Error("Invalid session data");
      }
    } catch (error) {
      console.error("Error loading chat session:", error);
      showToast(error.message, "error");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle copy to clipboard
  const handleCopyToClipboard = (text) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setToast({
          isVisible: true,
          message: "Copied to clipboard!",
          type: "success",
        });

        // Automatically hide toast after 3 seconds
        setTimeout(() => {
          setToast({
            ...toast,
            isVisible: false,
          });
        }, 1000);
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
        setToast({
          isVisible: true,
          message: "Failed to copy text.",
          type: "error",
        });

        // Automatically hide error toast after 3 seconds
        setTimeout(() => {
          setToast({
            ...toast,
            isVisible: false,
          });
        }, 1000);
      });
  };

  // Add state to track message feedback
  const [messageFeedback, setMessageFeedback] = useState({});

  // Handle feedback
  const handleFeedback = (messageId, feedbackType) => {
    // console.log(`Feedback for message ${messageId}: ${feedbackType}`);

    // Update feedback state to highlight the selected button
    setMessageFeedback((prev) => ({
      ...prev,
      [messageId]: feedbackType,
    }));

    // Show thank you toast based on feedback type
    setToast({
      isVisible: true,
      message:
        feedbackType === "thumbs-up"
          ? "Thank you for your positive feedback!"
          : "Thank you for your feedback. We're working on improving every day.",
      type: feedbackType === "thumbs-up" ? "success" : "info",
    });

    // Automatically hide toast after 2 seconds
    setTimeout(() => {
      setToast((prev) => ({
        ...prev,
        isVisible: false,
      }));
    }, 2000);

    // Here you would normally send this feedback to your backend API
    // Example: sendFeedbackToServer(messageId, feedbackType);
  };

  console.log("messages:", messages);

  return (
    <div
      className={`flex flex-col h-screen ${
        isDarkMode ? "bg-[#0b0e11] text-white" : "bg-white text-gray-900"
      } ${isKeyboardVisible ? "keyboard-visible" : ""}`}
    >
      {/* SEO */}
      <HelmetProvider>
        <title>
          BlippAI – Your Personal AI Companion for Real-Time Insights
        </title>
        <meta
          name="description"
          content="BlippAI is your personal AI assistant that delivers real-time insights from the web. Explore smarter search, instant answers, and more."
        />
      </HelmetProvider>

      {/* History Panel */}
      <AnimatePresence>
        <HistoryPanel
          isDarkMode={isDarkMode}
          isOpen={isHistoryPanelOpen}
          onClose={() => setIsHistoryPanelOpen(false)}
          onSelectSession={handleSelectSession}
          onClearHistory={handleClearConversations}
          currentSessionId={currentSessionId}
          refreshHistory={refreshHistory}
          onDeleteSession={handleDeleteSession}
        />
      </AnimatePresence>

      {/* Header - Fixed in mobile view */}
      <header
        className={`border-b ${
          isDarkMode ? "border-gray-700" : "border-gray-200"
        } py-3 px-4 flex justify-between items-center ${
          isMobile ? "fixed top-0 left-0 right-0 z-20" : ""
        } ${isDarkMode ? "bg-[#0b0e11]" : "bg-white"}`}
      >
        <div className="flex items-center gap-2">
          {/* History toggle button */}
          <button
            onClick={toggleHistoryPanel}
            className={`p-2 rounded-full ${
              isDarkMode
                ? "hover:bg-gray-800 text-gray-300"
                : "hover:bg-gray-100 text-gray-600"
            } ${
              isHistoryPanelOpen
                ? isDarkMode
                  ? "bg-gray-800"
                  : "bg-gray-100"
                : ""
            }`}
            aria-label="Toggle history"
          >
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              className="icon-xl-heavy"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M8.85719 3H15.1428C16.2266 2.99999 17.1007 2.99998 17.8086 3.05782C18.5375 3.11737 19.1777 3.24318 19.77 3.54497C20.7108 4.02433 21.4757 4.78924 21.955 5.73005C22.2568 6.32234 22.3826 6.96253 22.4422 7.69138C22.5 8.39925 22.5 9.27339 22.5 10.3572V13.6428C22.5 14.7266 22.5 15.6008 22.4422 16.3086C22.3826 17.0375 22.2568 17.6777 21.955 18.27C21.4757 19.2108 20.7108 19.9757 19.77 20.455C19.1777 20.7568 18.5375 20.8826 17.8086 20.9422C17.1008 21 16.2266 21 15.1428 21H8.85717C7.77339 21 6.89925 21 6.19138 20.9422C5.46253 20.8826 4.82234 20.7568 4.23005 20.455C3.28924 19.9757 2.52433 19.2108 2.04497 18.27C1.74318 17.6777 1.61737 17.0375 1.55782 16.3086C1.49998 15.6007 1.49999 14.7266 1.5 13.6428V10.3572C1.49999 9.27341 1.49998 8.39926 1.55782 7.69138C1.61737 6.96253 1.74318 6.32234 2.04497 5.73005C2.52433 4.78924 3.28924 4.02433 4.23005 3.54497C4.82234 3.24318 5.46253 3.11737 6.19138 3.05782C6.89926 2.99998 7.77341 2.99999 8.85719 3ZM6.35424 5.05118C5.74907 5.10062 5.40138 5.19279 5.13803 5.32698C4.57354 5.6146 4.1146 6.07354 3.82698 6.63803C3.69279 6.90138 3.60062 7.24907 3.55118 7.85424C3.50078 8.47108 3.5 9.26339 3.5 10.4V13.6C3.5 14.7366 3.50078 15.5289 3.55118 16.1458C3.60062 16.7509 3.69279 17.0986 3.82698 17.362C4.1146 17.9265 4.57354 18.3854 5.13803 18.673C5.40138 18.8072 5.74907 18.8994 6.35424 18.9488C6.97108 18.9992 7.76339 19 8.9 19H9.5V5H8.9C7.76339 5 6.97108 5.00078 6.35424 5.05118ZM11.5 5V19H15.1C16.2366 19 17.0289 18.9992 17.6458 18.9488C18.2509 18.8994 18.5986 18.8072 18.862 18.673C19.4265 18.3854 19.8854 17.9265 20.173 17.362C20.3072 17.0986 20.3994 16.7509 20.4488 16.1458C20.4992 15.5289 20.5 14.7366 20.5 13.6V10.4C20.5 9.26339 20.4992 8.47108 20.4488 7.85424C20.3994 7.24907 20.3072 6.90138 20.173 6.63803C19.8854 6.07354 19.4265 5.6146 18.862 5.32698C18.5986 5.19279 18.2509 5.10062 17.6458 5.05118C17.0289 5.00078 16.2366 5 15.1 5H11.5ZM5 8.5C5 7.94772 5.44772 7.5 6 7.5H7C7.55229 7.5 8 7.94772 8 8.5C8 9.05229 7.55229 9.5 7 9.5H6C5.44772 9.5 5 9.05229 5 8.5ZM5 12C5 11.4477 5.44772 11 6 11H7C7.55229 11 8 11.4477 8 12C8 12.5523 7.55229 13 7 13H6C5.44772 13 5 12.5523 5 12Z"
                fill="currentColor"
              ></path>
            </svg>
          </button>
          {/* Wordmark is drawn in --foreground, which is the dark-theme ink; flip it for light mode */}
          <a
            href="/"
            className="flex items-center"
            style={isDarkMode ? undefined : { "--foreground": "211 22% 5%" }}
          >
            <BlippLogo className="h-8 w-auto" />
          </a>

          {/* Test mode indicator */}
          {useTestChat && (
            <span
              className={`ml-2 text-xs px-2 py-1 rounded-full ${
                isDarkMode
                  ? "bg-purple-800/50 text-purple-200"
                  : "bg-purple-100 text-purple-800"
              }`}
            >
              TEST
            </span>
          )}
        </div>
        {/* Dropdown menu 
        {currentUser && dropdownOpen && (
          <div className="relative">
            <div
              className={`absolute right-0 mt-2 w-48 ${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'} rounded-lg shadow-lg z-20`}
            >
              <button
                onClick={handleLogout}
                className={`w-full text-left px-4 py-2 rounded-lg ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
              >
                Log Out
              </button>
            </div>
          </div>
        )}
          */}

        <div className="flex items-center gap-2">
          {/* Language Dropdown */}
          {/* <LanguageDropdown
            language={language}
            onChangeLanguage={setLanguage}
            isDarkMode={isDarkMode}
            position="bottom-left"
            historyPanelOpen={isHistoryPanelOpen}
          /> */}

          {isAnonymous && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleLogin}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  isDarkMode
                    ? "bg-primary text-primary-foreground hover:bg-blue-400"
                    : "bg-blue-600 text-white hover:bg-blue-700"
                }`}
              >
                Login
              </button>
              {/* <button
                onClick={handleSignup}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  isDarkMode ? 'bg-gray-800 text-white hover:bg-gray-700' : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                Signup
              </button> */}
            </div>
          )}

          {/* Theme Toggle Button */}
          <button
            onClick={() => setIsDarkMode(!isDarkMode)}
            className={`p-2 rounded-full ${
              isDarkMode
                ? "bg-gray-700 text-yellow-400 hover:bg-gray-600"
                : "bg-gray-200 text-gray-700 hover:bg-gray-300"
            }`}
            aria-label={
              isDarkMode ? "Switch to light mode" : "Switch to dark mode"
            }
          >
            <i className={`fas ${isDarkMode ? "fa-sun" : "fa-moon"}`}></i>
          </button>
          {/* User Menu: Only show if logged in and not anonymous */}
          <UserMenu isDarkMode={isDarkMode} />

          {/* Command menu toggle */}
          {/* <button 
            onClick={() => setShowCommandMenu(!showCommandMenu)}
            className={`p-2 rounded-full ${
              isDarkMode ? 'hover:bg-gray-800 text-gray-300' : 'hover:bg-gray-100 text-gray-600'
            }`}
            aria-label="Menu"
          >
            <i className="fas fa-ellipsis-h"></i>
          </button> */}
        </div>
      </header>

      {/* Command Menu */}
      <AnimatePresence>
        {showCommandMenu && (
          <CommandMenu
            isDarkMode={isDarkMode}
            onClose={() => setShowCommandMenu(false)}
            commands={[
              {
                icon: "plus",
                label: getTranslation("New Chat"),
                action: handleNewChat,
              },
              {
                icon: "trash",
                label: getTranslation("Clear Conversations"),
                action: handleClearConversations,
              },
              {
                icon: "flask",
                label: getTranslation(
                  useTestChat ? "Normal Chat Mode" : "Test Chat Mode"
                ),
                action: toggleTestChatMode,
              },
              {
                icon: isDarkMode ? "sun" : "moon",
                label: getTranslation(isDarkMode ? "Light mode" : "Dark mode"),
                action: () => setIsDarkMode(!isDarkMode),
              },
              {
                icon: "sign-out-alt",
                label: getTranslation("Log out"),
                action: () => navigate("/login"),
              },
            ]}
          />
        )}
      </AnimatePresence>

      {/* Authentication Popup - REMOVED
      <AnimatePresence>
        {showAuthPopup && (
          ...popup content removed...
        )}
      </AnimatePresence>
      */}

      {/* Main Content Area with padding for fixed header and footer on mobile */}
      <main
        ref={chatContainerRef}
        className={`flex-1 overflow-y-auto mobile-chat-container ${
          isDarkMode ? "bg-[#0b0e11]" : "bg-[#f9fafc]"
        } ${isMobile ? "pt-[90px] pb-[180px]" : "px-4 py-6"} ${
          isKeyboardVisible ? "keyboard-visible-padding" : ""
        }`}
        style={{
          backgroundImage: isDarkMode
            ? `radial-gradient(circle at center, #111418 0%, #0b0e11 100%)`
            : `radial-gradient(circle at center, #FFFFFF 0%, #f9fafc 100%)`,
        }}
      >
        <div className={`max-w-3xl mx-auto ${isMobile ? "px-4" : ""}`}>
          {messages.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center py-8">
              {/* <div className={`w-16 h-16 mb-6 rounded-full flex items-center justify-center ${
                isDarkMode ? 'bg-gray-800' : 'bg-gray-100'
              }`}>
                <i className={`fas fa-robot text-2xl ${
                  isDarkMode ? 'text-blue-400' : 'text-blue-500'
                }`}></i>
              </div> */}
              <h2 className="text-2xl font-bold mb-2">
                {getGreeting(language)} 👋
              </h2>
              <h3 className="text-xl font-medium mb-4">
                I'm BlippAI {useTestChat && "(Test Mode)"}
              </h3>
              <p
                className={`text-center mb-8 ${
                  isDarkMode ? "text-gray-400" : "text-gray-500"
                }`}
              >
                {getTranslation(
                  "Your multi-language AI assistant! - Your guide, friend, and sidekick all in one."
                )}
              </p>
            </div>
          ) : (
            <div className="space-y-1">
              {messages.map((message, index) => (
                <div
                  key={message.id}
                  className={`flex flex-col ${
                    message.role === "user" ? "items-end group" : "items-start"
                  } ${language === "ar" ? "flex-row-reverse" : ""}`}
                >
                  <div
                    className={`rounded-2xl p-2.5 ${
                      message.role === "user"
                        ? isDarkMode
                          ? "bg-gray-800 text-gray-50 border border-white/10"
                          : "bg-blue-500 text-white"
                        : message.isError
                        ? isDarkMode
                          ? "bg-red-800/20 text-red-200 border border-red-800/30"
                          : "bg-red-50 text-red-600 border border-red-100"
                        : isDarkMode
                        ? "text-white"
                        : "text-gray-800"
                    }`}
                    style={{ direction: language === "ar" ? "rtl" : "ltr" }}
                  >
                    {message.file && message.file.type === "image" ? (
                      <>
                        <div className="mb-2 rounded-lg overflow-hidden">
                          <img
                            src={message.file.data}
                            alt="User uploaded"
                            className="max-w-full h-auto"
                          />
                        </div>
                        <MarkdownRenderer
                          content={message.content}
                          isDarkMode={isDarkMode}
                          sources={message.sources || []}
                          isStreaming={message.isStreaming}
                        />
                      </>
                    ) : message.file ? (
                      <MarkdownRenderer
                        content={message.content}
                        isDarkMode={isDarkMode}
                        sources={message.sources || []}
                        isStreaming={message.isStreaming}
                      />
                    ) : (
                      <MarkdownRenderer
                        content={message.content}
                        isDarkMode={isDarkMode}
                        sources={message.sources || []}
                        isStreaming={message.isStreaming}
                      />
                    )}
                  </div>
                  {/* Display SuggestedQuestions directly after assistant's message content */}
                  {message.role === "assistant" &&
                    !message.isStreaming &&
                    !message.isError &&
                    index === messages.length - 1 &&
                    suggestedQuestions.length > 0 && (
                      <div className="mt-2 w-full">
                        <SuggestedQuestions
                          questions={suggestedQuestions}
                          onQuestionClick={(question) => {
                            setQuery(question);
                            handleSubmit();
                            setSuggestedQuestions([]); // Clear after selection
                          }}
                          isDarkMode={isDarkMode}
                          language={language}
                        />
                      </div>
                    )}

                  {/* Action buttons only visible after message is fully complete */}
                  {message.role === "assistant" && !message.isError && (
                    <div
                      className={`mt-3 flex items-center gap-2 ${
                        language === "ar" ? "justify-end" : ""
                      }`}
                    >
                      {(isStreaming && index === messages.length - 1) ||
                      message.isStreaming ? (
                        <div
                          className={`relative w-10 h-10 flex items-center justify-center`}
                        >
                          <div className="absolute inset-0 flex items-center justify-center left-[50%] top-[50%] transform -translate-x-1/2 -translate-y-1/2">
                            <BlippMark className="h-8 w-8 text-primary animate-spin duration-1000" />
                          </div>
                          <div
                            className={`absolute inset-0 ${
                              isDarkMode
                                ? "border-t-blue-400"
                                : "border-t-blue-500"
                            } border-t-2 rounded-full animate-spin`}
                          ></div>
                        </div>
                      ) : (
                        !message.isStreaming && (
                          <motion.div
                            className="flex items-center gap-2 mb-4"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.1 }}
                          >
                            <motion.div
                              initial={{ opacity: 0, y: 30 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{
                                duration: 0.6,
                                delay: 0.2,
                                ease: "easeOut",
                              }}
                            >
                              <TextToSpeech
                                text={message.content}
                                isDarkMode={isDarkMode}
                                language={language}
                              />
                            </motion.div>

                            {/* Copy button with icon and text - styled like in the image */}
                            <motion.button
                              initial={{ opacity: 0, y: 30 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{
                                duration: 0.6,
                                delay: 0.35,
                                ease: "easeOut",
                              }}
                              onClick={() =>
                                handleCopyToClipboard(message.content)
                              }
                              className={`px-3 py-2 rounded-lg transition-all duration-200 border flex items-center gap-2 ${
                                isDarkMode
                                  ? "border-gray-700 hover:border-gray-600 text-gray-400 hover:text-gray-300 hover:bg-gray-800/50"
                                  : "border-gray-200 hover:border-gray-300 text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                              }`}
                              title="Copy to clipboard"
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <i className="fas fa-copy text-xs"></i>
                              <span className="text-sm">Copy</span>
                            </motion.button>

                            {/* Thumbs up button with icon and text - styled like in the image */}
                            <motion.button
                              initial={{ opacity: 0, y: 30 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{
                                duration: 0.6,
                                delay: 0.5,
                                ease: "easeOut",
                              }}
                              onClick={() =>
                                handleFeedback(message.id, "thumbs-up")
                              }
                              className={`px-3 py-2 rounded-lg transition-all duration-200 border flex items-center gap-2 ${
                                messageFeedback[message.id] === "thumbs-up"
                                  ? isDarkMode
                                    ? "border-green-600 bg-green-500/20 text-green-400"
                                    : "border-green-600 bg-green-100 text-green-600"
                                  : isDarkMode
                                  ? "border-gray-700 hover:border-gray-600 text-gray-400 hover:text-green-400 hover:bg-gray-800/50"
                                  : "border-gray-200 hover:border-gray-300 text-gray-500 hover:text-green-600 hover:bg-gray-50"
                              }`}
                              title="Helpful response"
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <i className="fas fa-thumbs-up text-xs"></i>
                              <span className="text-sm">Good</span>
                            </motion.button>

                            {/* Thumbs down button with icon and text - styled like in the image */}
                            <motion.button
                              initial={{ opacity: 0, y: 30 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{
                                duration: 0.6,
                                delay: 0.65,
                                ease: "easeOut",
                              }}
                              onClick={() =>
                                handleFeedback(message.id, "thumbs-down")
                              }
                              className={`px-3 py-2 rounded-lg transition-all duration-200 border flex items-center gap-2 ${
                                messageFeedback[message.id] === "thumbs-down"
                                  ? isDarkMode
                                    ? "border-red-600 bg-red-500/20 text-red-400"
                                    : "border-red-600 bg-red-100 text-red-600"
                                  : isDarkMode
                                  ? "border-gray-700 hover:border-gray-600 text-gray-400 hover:text-red-400 hover:bg-gray-800/50"
                                  : "border-gray-200 hover:border-gray-300 text-gray-500 hover:text-red-600 hover:bg-gray-50"
                              }`}
                              title="Not helpful response"
                              whileHover={{ scale: 1.02 }}
                              whileTap={{ scale: 0.98 }}
                            >
                              <i className="fas fa-thumbs-down text-xs"></i>
                              <span className="text-sm">Bad</span>
                            </motion.button>
                          </motion.div>
                        )
                      )}
                    </div>
                  )}
                  {message.role === "user" && (
                    <div
                      className={`mt-2 flex items-center ${
                        language === "ar" ? "justify-start" : "justify-end"
                      } opacity-0 group-hover:opacity-100 transition-opacity duration-200`}
                    >
                      <button
                        onClick={() => handleCopyToClipboard(message.content)}
                        className={`p-1.5 rounded-md transition-all duration-200 ${
                          isDarkMode
                            ? "hover:bg-gray-800 text-gray-400 hover:text-gray-300"
                            : "hover:bg-gray-100 text-gray-500 hover:text-gray-700"
                        }`}
                        title="Copy to clipboard"
                      >
                        <i className="fas fa-copy text-xs"></i>
                      </button>
                    </div>
                  )}
                </div>
              ))}
              <div ref={(node) => setMessagesEndRef(node)} />
              <div className="h-[80px] w-full" aria-hidden="true"></div>
            </div>
          )}
        </div>
      </main>

      {/* Chat Input Area - Fixed in mobile view */}
      <div
        className={`border-t ${
          isDarkMode
            ? "border-gray-800 bg-[#0b0e11]"
            : "border-gray-200 bg-white"
        } ${isMobile ? "fixed bottom-0 left-0 right-0 z-10 p-1.5" : "p-2.5"}`}
      >
        <div className="max-w-3xl mx-auto">
          <ChatInput
            query={query}
            setQuery={setQuery}
            handleSubmit={handleSubmit}
            handleUploadClick={handleUploadClick}
            loadingResponse={isLoading}
            getTranslation={getTranslation}
            isDarkMode={isDarkMode}
            language={language}
            messages={messages}
            pendingFiles={pendingFiles}
          />
          {/* <footer className="mt-2 text-center text-sm">
            <a href="/" className={`underline ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
              Blipp AI Home
            </a>
            <span className="mx-2">|</span>
            <a href="/about" className={`underline ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
              About Blipp AI
            </a>
            <span className="mx-2">|</span>
            <a href="/features" className={`underline ${isDarkMode ? 'text-blue-300' : 'text-blue-600'}`}>
              Blipp AI Features
            </a>
          </footer> */}
        </div>
      </div>

      {/* Toast Notification */}
      <Toast
        isVisible={toast.isVisible}
        message={toast.message}
        type={toast.type}
      />
    </div>
  );
};

export default MainComponent;
